import React, { useState, useEffect } from 'react';
import { Progress, Badge, FloatButton, Tooltip } from 'antd';
import { SyncOutlined } from '@ant-design/icons';

const PersistentProgressIndicator = () => {
  const [uploads, setUploads] = useState([]);
  const [visible, setVisible] = useState(false);

  // Load from localStorage on mount
  useEffect(() => {
    const savedUploads = localStorage.getItem('persistentUploads');
    if (savedUploads) {
      const parsed = JSON.parse(savedUploads);
      setUploads(parsed);
      setVisible(parsed.length > 0);
    }
  }, []);

  // Save to localStorage whenever uploads change
  useEffect(() => {
    if (uploads.length > 0) {
      localStorage.setItem('persistentUploads', JSON.stringify(uploads));
    } else {
      localStorage.removeItem('persistentUploads');
    }
    setVisible(uploads.length > 0);
  }, [uploads]);

  // Clean up completed uploads older than 5 minutes
  useEffect(() => {
    const interval = setInterval(() => {
      const now = Date.now();
      setUploads(prev => prev.filter(upload => {
        const age = now - upload.startTime;
        return upload.status !== 'completed' || age < 300000; // 5 minutes
      }));
    }, 30000); // Check every 30 seconds

    return () => clearInterval(interval);
  }, []);

  // Add new upload
  const addUpload = (filename, type = 'upload') => {
    const newUpload = {
      id: Date.now(),
      filename,
      type, // 'upload', 'processing', 'ai_extraction'
      status: 'pending',
      progress: 0,
      startTime: Date.now(),
      message: 'Starting...'
    };
    
    setUploads(prev => [...prev, newUpload]);
    return newUpload.id;
  };

  // Update upload progress
  const updateUpload = (id, updates) => {
    setUploads(prev => prev.map(upload => 
      upload.id === id ? { ...upload, ...updates } : upload
    ));
  };

  // Complete upload
  const completeUpload = (id, success = true) => {
    updateUpload(id, {
      status: success ? 'completed' : 'failed',
      progress: 100,
      message: success ? 'Completed!' : 'Failed!'
    });
  };

  // Clear all completed uploads
  const clearCompleted = () => {
    setUploads(prev => prev.filter(upload => upload.status !== 'completed'));
  };

  // Make functions global for other components to call
  useEffect(() => {
    window.persistentProgress = {
      addUpload,
      updateUpload,
      completeUpload,
      clearCompleted
    };
  }, []);

  if (!visible || uploads.length === 0) return null;

  const activeUploads = uploads.filter(u => u.status === 'pending' || u.status === 'processing');
  const completedUploads = uploads.filter(u => u.status === 'completed');
  const failedUploads = uploads.filter(u => u.status === 'failed');

  // Calculate overall progress
  const overallProgress = uploads.length > 0 
    ? uploads.reduce((sum, u) => sum + u.progress, 0) / uploads.length 
    : 0;

  const getStatusColor = (status) => {
    switch (status) {
      case 'completed': return '#52c41a';
      case 'failed': return '#ff4d4f';
      case 'processing': return '#1890ff';
      default: return '#faad14';
    }
  };

  const getContent = () => (
    <div style={{ 
      width: 300, 
      maxHeight: 400, 
      overflow: 'auto',
      padding: '16px',
      background: 'white',
      borderRadius: '8px',
      boxShadow: '0 4px 12px rgba(0,0,0,0.15)'
    }}>
      <div style={{ marginBottom: 16, fontWeight: 'bold' }}>
        Upload Progress ({uploads.length})
      </div>
      
      {uploads.map(upload => (
        <div key={upload.id} style={{ marginBottom: 12 }}>
          <div style={{ 
            display: 'flex', 
            justifyContent: 'space-between', 
            alignItems: 'center',
            marginBottom: 4
          }}>
            <span style={{ fontSize: 12, fontWeight: 500 }}>
              {upload.filename}
            </span>
            <Badge 
              status={upload.status === 'completed' ? 'success' : upload.status === 'failed' ? 'error' : 'processing'}
              text={`${upload.progress}%`}
            />
          </div>
          
          <Progress 
            percent={upload.progress}
            size="small"
            strokeColor={getStatusColor(upload.status)}
            showInfo={false}
          />
          
          <div style={{ fontSize: 11, color: '#666', marginTop: 2 }}>
            {upload.message}
          </div>
        </div>
      ))}
      
      {completedUploads.length > 0 && (
        <div style={{ marginTop: 16, paddingTop: 16, borderTop: '1px solid #f0f0f0' }}>
          <button
            onClick={clearCompleted}
            style={{
              background: '#f5f5f5',
              border: 'none',
              padding: '4px 8px',
              borderRadius: '4px',
              cursor: 'pointer',
              fontSize: 12
            }}
          >
            Clear Completed ({completedUploads.length})
          </button>
        </div>
      )}
    </div>
  );

  return (
    <div style={{ position: 'fixed', bottom: 20, right: 20, zIndex: 1000 }}>
      <FloatButton.Group
        trigger="click"
        type="primary"
        icon={<SyncOutlined spin={activeUploads.length > 0} />}
        tooltip="Upload Progress"
        badge={{ count: activeUploads.length, size: 'small' }}
      >
        <FloatButton.BackTop visibilityHeight={0} />
        {getContent()}
      </FloatButton.Group>
    </div>
  );
};

export default PersistentProgressIndicator;
